<script lang="ts">
	import { min, max, from, to, resetPage } from '../stores/filter';
	import RangeSlider from 'svelte-range-slider-pips';

	let { facetDistribution }: { facetDistribution: Record<string, number> } =
		$props();

	const width = 280;
	const height = 68;

	const peak = $derived(() => {
		const amounts = Object.values<number>(facetDistribution);
		return Math.max(...amounts);
	});

	const d = $derived(() => {
		const points = Object.values(
			Array($max - $min + 1)
				.fill(0)
				.reduce((acc, cur, idx) => {
					const year = idx + $min;
					return Object.assign(acc, {
						[year]: facetDistribution.hasOwnProperty(year)
							? facetDistribution[year]
							: 0,
					});
				}, {}),
		).map(value => (value / peak()) * height);

		return (
			`M0 ${height}L` +
			points
				.map(
					(point, index) =>
						`${(index / (points.length - 1)) * width} ${height - point}`,
				)
				.join('L') +
			`L${width} ${height}z`
		);
	});

	const onStop = (event: CustomEvent) => {
		[$from, $to] = event.detail.values;
		resetPage();
	};
</script>

<div class="content filter__facet">
	<svg
		viewBox="0 0 {width} {height}"
		{width}
		{height}
		preserveAspectRatio="none"
	>
		<path d={d()} fill="currentColor" mask="url(#rangeMask)"></path>
		<rect
			x="0"
			width={(($from - $min) / ($max - $min)) * width}
			height="100%"
			fill="rgba(0,0,0,0.75)"
		></rect>
		<rect
			x={(($to - $min) / ($max - $min)) * width}
			width={(($max - $to) / ($max - $min)) * width}
			height="100%"
			fill="rgba(0,0,0,0.75)"
		></rect>
	</svg>

	<RangeSlider
		range={true}
		values={[$from, $to]}
		min={$min}
		max={$max}
		pushy={true}
		on:stop={onStop}
	/>

	<input type="number" required bind:value={$from} min={$min} max={$max} />
	<input type="number" required bind:value={$to} min={$min} max={$max} />
</div>

<style>
	path {
		transition: d 200ms ease-out;
	}
	:global(.rangeSlider) {
		--range-slider: currentColor;
		--range-handle: var(--range-slider);
		--range-handle-focus: var(--range-slider);
		--range-handle-inactive: var(--range-slider);
		--range-range-inactive: var(--range-slider);
		block-size: 2px !important;
		inline-size: 100%;
		margin-block: 1rem !important;
		border-radius: 0 !important;
		margin-inline: 0 !important;
		background: transparent
			repeating-linear-gradient(
				to right,
				white,
				white 2px,
				transparent 2px,
				transparent 7px
			) !important;
	}
	:global(.rangeSlider .rangeHandle) {
		top: 0 !important;
		width: 1.5rem !important;
		height: 1.5rem !important;
		cursor: pointer;
	}
	:global(.rangeSlider .rangeHandle::after) {
		content: '';
		left: 1px;
		top: 1px;
		bottom: 1px;
		right: 1px;
		box-shadow: 0 0 0 2px var(--color-blue);
		position: absolute;
		border-radius: 10em;
	}
	:global(.rangeSlider .rangeHandle::before) {
		border-radius: 0 !important;
		width: 2px !important;
		left: calc(50% - 1px) !important;
	}
	:global(.rangeSlider .rangeHandle:hover::before) {
		box-shadow: 0 0 0 6px var(--handle-border) !important;
	}
	:global(.rangeSlider .rangeHandle.press::before) {
		box-shadow: 0 0 0 4px var(--handle-border) !important;
	}
	:global(.rangeSlider .rangeNub) {
		border-radius: 0 !important;
		transform: none !important;
		width: 2px !important;
		left: calc(50% - 1px) !important;
		height: 1.5rem !important;
	}
	:global(.rangeSlider .rangeBar) {
		height: 2px !important;
	}
</style>
